import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DESKTOP_PADDING, MOBILE_PADDING } from 'homepage/styles/constants';
import { fetchBrandListSection } from 'homepage/services/homepageApi';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { setCurrentFeedData } from 'store/actions/homepageActions';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { CuratedLinkTableCarousel } from 'homepage/components/brandsListSection/CuratedLinkTableCarousel';

import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { IFeedSection, ISubSection } from 'homepage/types/Feed';

interface IBrandListSection {
  constructorUserParams?: IConstructorUserParams;
  requestMade?: boolean;
  section: IFeedSection;
  sectionIndex: number;
  subsections: ISubSection[];
  moduleTrackingData: IModuleTrackingData;
}

export const BrandListSection = ({
  constructorUserParams,
  moduleTrackingData,
  requestMade = false,
  section,
  sectionIndex,
  subsections,
}: IBrandListSection) => {
  const isMounted = useIsMounted();
  const storeDispatch = useStoreDispatch();

  const { signal } = useAbortController()();

  const { t } = useTranslation();

  const [brands, setBrands] = useState(subsections);

  useEffect(() => {
    const getBrands = async (abortSignal) => {
      const response = await fetchBrandListSection(
        section,
        subsections,
        constructorUserParams,
        abortSignal,
      );
      if (isMounted()) {
        setBrands(response?.subsections);
      }
    };
    if (!requestMade) {
      void getBrands(signal);
    }
  }, []);

  useEffect(() => {
    if (brands && isMounted()) {
      storeDispatch(setCurrentFeedData({ index: sectionIndex, subsections: brands }));
    }
  }, [brands]);

  return (
    <BrandListSection.Container>
      <BrandListSection.Header>
        <BrandListSection.Title>{t(`homepage.module.brands`, 'Brands')}</BrandListSection.Title>
      </BrandListSection.Header>
      <CuratedLinkTableCarousel curatedLinks={brands} moduleTrackingData={moduleTrackingData} />
    </BrandListSection.Container>
  );
};

BrandListSection.Container = styled.div`
  margin: 32px 0px;
  width: 100%;
  max-width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  padding-left: ${MOBILE_PADDING};
  ${hideScrollbars}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 60px 0px;
    padding-left: ${DESKTOP_PADDING};
  }
`;

BrandListSection.Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  padding-right: ${DESKTOP_PADDING};
`;

BrandListSection.Title = styled.h2`
  font-weight: 400;
  text-transform: capitalize;
  ${variableFontRegular({ size: '13px', height: '15px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    font-weight: 600;
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
  }
`;
